import React, { useEffect, useState, useRef } from 'react'
import PropTypes, { any } from 'prop-types'
import dayjs from 'dayjs'
import { DateField } from '@mui/x-date-pickers'
import { SearchIcon } from '../mui/Icons'
import { Box, MenuItem, Select, TextField } from '../mui/Core'
import { useTheme } from '../mui/Styles/index'
import Constants from '../../constants/en.json'
import { inputFormattingConstants } from '../../constants/input-formatting-constants'
import GenericAutocomplete from '../dropdowns/GenericAutocomplete/generic-autocomplete'
import { STATUS_INITIAL_STATE } from '../../constants/status'

function TableSearch({ returnSearch, searchByOptions, statusOptions }) {
  const theme = useTheme()
  const prevSearchTypeRef = useRef(null) // Stores the previously selected search type
  const prevSearchOptionRef = useRef(null) // Stores the previously selected search option
  const inputRef = useRef(null) // Reference to the input or date field element
  const [searchVal, setSearchVal] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [searchType, setSearchType] = useState('string')
  const [selectedDropdownOption, setSelectedDropdownOption] = useState('')
  const [selectedStatus, setSelectedStatus] = useState(STATUS_INITIAL_STATE)

  useEffect(() => {
    setSelectedDropdownOption(searchByOptions[selectedIndex].id)
  }, [searchByOptions, selectedIndex])

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      returnSearch(searchVal, selectedIndex)
    }
  }

  const handleInputOnChange = (event) => {
    setSearchVal(event.target.value)
  }

  const handleDateFieldOnChange = (newValue) => {
    setSearchVal(newValue ? newValue.format('YYYY-MM-DD') : '')
  }

  const handleMenuItemClick = (event) => {
    event.preventDefault()
    const index = searchByOptions.map((option) => option.id).indexOf(event.target.value)
    const searchValType = searchByOptions[index].type
    const searchOption = searchByOptions[index].id
    setSelectedDropdownOption(searchOption)
    setSearchType(searchValType)
    setSelectedIndex(index)
    setSelectedStatus(STATUS_INITIAL_STATE)

    setTimeout(() => {
      if (inputRef.current) {
        const isSwitchingToDateOrStatus =
          searchValType === 'date' ||
          searchOption === 'orderStatus' ||
          searchOption === 'paymentStatus'

        const isSwitchingFromDateOrStatus =
          prevSearchTypeRef.current === 'date' ||
          prevSearchOptionRef.current === 'orderStatus' ||
          prevSearchOptionRef.current === 'paymentStatus'
        // Reset value if: Switching to 'date' or 'status' OR Switching from 'date' or 'status' to some other type.
        if (isSwitchingToDateOrStatus || isSwitchingFromDateOrStatus) {
          setSearchVal('')
        } else {
          inputRef.current.select()
        }
        inputRef.current.focus()
      }
      // Update previous type and option reference
      prevSearchTypeRef.current = searchValType
      prevSearchOptionRef.current = searchOption
    }, 0)
  }

  const handleSelectStatus = (event) => {
    setSelectedStatus(event)
    returnSearch(event.id, selectedIndex)
  }

  const styledBorder = {
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.grey[700],
    border: '1px solid',
    borderRadius: theme.shape.borderRadius,
    height: '40px',
    minWidth: '300px',
    alignItems: 'center',
    display: 'flex',
    ':focus-within': {
      border: '2px solid',
      borderColor: `${theme.palette.secondary.main} !important`,
    },
  }

  const selectStyle = {
    fontSize: '16px',
    height: '39px',
    color: theme.palette.common.black,
    backgroundColor: 'transparent',
    borderColor: theme.palette.grey[700],
    justifyContent: 'flex-end',
    pt: 1,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.grey[700]} !important`,
      borderWidth: '1px !important',
    },
    '& fieldset': {
      border: 'none',
      borderRight: '2px solid',
      borderRadius: 0,
    },
  }

  const searchIconContainerStyle = {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    padding: '0 4px 0 10px',
    height: '34px',
    pointerEvents: 'none',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const textFieldStyle = {
    position: 'relative',
    height: '35px',
    width: '300px',
    minWidth: '100px',
    border: 'none',
    fontSize: '16px',
    fontFamily: 'AtlasGroteskRegular',
    paddingTop: '3px',
    backgroundColor: theme.palette.white.main,
    '& .MuiOutlinedInput-input': {
      padding: '6px',
      '&. Mui-focused fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
    '& fieldset': {
      border: 'none',
    },
    '& label': {
      top: 'auto',
      bottom: '22px',
      left: '-18px',
    },
    '& .MuiInputLabel-shrink': {
      backgroundColor: `${theme.palette.common.white}`,
      padding: '0 2px',
      top: '0 !important',
      bottom: 'auto',
      left: '-44px !important',
    },
    '& input::placeholder': {
      color: `${theme.palette.common.black}`,
      opacity: 1,
    },
  }

  const menuItemStyle = {
    justifyContent: 'flex-end',
  }

  const autoCompleteStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      '& fieldset': { border: 'none' },
      '&:hover fieldset': { border: 'none' },
      '&.Mui-focused fieldset': { border: 'none' },
    },
  }

  return (
    <Box sx={styledBorder}>
      <Select value={selectedDropdownOption} sx={selectStyle} onChange={handleMenuItemClick}>
        {searchByOptions.map((option) => {
          return (
            <MenuItem sx={menuItemStyle} key={option.id} value={option.id} type={option.type}>
              {option.text}
            </MenuItem>
          )
        })}
      </Select>
      <Box sx={searchIconContainerStyle}>
        <SearchIcon />
      </Box>
      {searchType === 'dropdown' && (
        <GenericAutocomplete
          width='250px'
          dropdownOptions={statusOptions}
          selectedOption={selectedStatus}
          handleSelectOption={handleSelectStatus}
          rootStyle={autoCompleteStyle}
        />
      )}
      {searchType === 'string' && (
        <TextField
          inputRef={inputRef}
          sx={textFieldStyle}
          type='text'
          label={Constants.tableSearch.SEARCH}
          value={searchVal}
          onChange={handleInputOnChange}
          onKeyDown={handleKeyPress}
        />
      )}
      {searchType === 'date' && (
        <DateField
          name='dateField'
          size='small'
          inputRef={inputRef}
          format={inputFormattingConstants.dateFormats.MMDDYYYY}
          sx={textFieldStyle}
          maxDate={dayjs('9999-12-31')}
          minDate={dayjs('1900-01-01')}
          value={searchVal ? dayjs(searchVal) : null}
          onChange={handleDateFieldOnChange}
          onKeyDown={(e) => handleKeyPress(e)}
          slotProps={{
            textField: {
              inputProps: {
                'aria-label': 'search',
              },
            },
          }}
        />
      )}
    </Box>
  )
}

TableSearch.propTypes = {
  returnSearch: PropTypes.func.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.objectOf(any)),
}

export default TableSearch
